/**
 * TOAST COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Liquid Glass material with blur
 * - Smooth spring animations
 * - Auto-dismiss with progress indicator
 * - Multiple toast types with color tinting
 * - Swipe to dismiss
 * - Stacking support
 */

import { useEffect, useState, useCallback, createContext, useContext, ReactNode } from 'react'
import { motion, AnimatePresence, PanInfo } from 'framer-motion'
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react'
import { cn } from '../../lib/utils'

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastProps {
  id: string
  message: string
  type: ToastType
  title?: string
  duration?: number
  onClose: (id: string) => void
  action?: {
    label: string
    onClick: () => void
  }
}

const iconMap = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info
}

const colorMap = {
  success: {
    icon: 'text-green dark:text-green-dark',
    bg: 'bg-green-glass dark:bg-green-glass',
    progress: 'bg-green dark:bg-green-dark'
  },
  error: {
    icon: 'text-red dark:text-red-dark',
    bg: 'bg-red-glass dark:bg-red-glass',
    progress: 'bg-red dark:bg-red-dark'
  },
  warning: {
    icon: 'text-orange dark:text-orange-dark',
    bg: 'bg-orange-glass dark:bg-orange-glass',
    progress: 'bg-orange dark:bg-orange-dark'
  },
  info: {
    icon: 'text-blue dark:text-blue-dark',
    bg: 'bg-blue-glass dark:bg-blue-glass',
    progress: 'bg-blue dark:bg-blue-dark'
  }
}

export function Toast({
  id,
  message,
  type,
  title,
  duration = 4000,
  onClose,
  action
}: ToastProps) {
  const [progress, setProgress] = useState(100)
  const [isPaused, setIsPaused] = useState(false)

  const Icon = iconMap[type]
  const colors = colorMap[type]

  useEffect(() => {
    if (isPaused || duration === 0) return

    const startTime = Date.now()
    const endTime = startTime + duration

    const updateProgress = () => {
      const now = Date.now()
      const remaining = Math.max(0, endTime - now)
      const newProgress = (remaining / duration) * 100

      setProgress(newProgress)

      if (newProgress > 0) {
        requestAnimationFrame(updateProgress)
      } else {
        onClose(id)
      }
    }

    const animationFrame = requestAnimationFrame(updateProgress)
    return () => cancelAnimationFrame(animationFrame)
  }, [duration, onClose, id, isPaused])

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (Math.abs(info.offset.x) > 100 || Math.abs(info.velocity.x) > 500) {
      onClose(id)
    }
  }

  // Spring animation config
  const springConfig = {
    type: "spring",
    damping: 25,
    stiffness: 300,
    mass: 0.8
  }

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 50, scale: 0.9, x: 0 }}
      animate={{ opacity: 1, y: 0, scale: 1, x: 0 }}
      exit={{ opacity: 0, scale: 0.9, x: 100 }}
      transition={springConfig}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.5}
      onDragEnd={handleDragEnd}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className="w-full max-w-sm cursor-grab active:cursor-grabbing"
    >
      <div
        className={cn(
          'relative overflow-hidden',
          'liquid-material-thick liquid-lens rounded-2xl',
          'shadow-liquid-lg dark:shadow-dark-liquid-lg',
          'border border-black/[0.06] dark:border-white/[0.08]'
        )}
      >
        {/* Content */}
        <div className="flex items-start gap-3 p-4">
          {/* Icon */}
          <div className={cn(
            'flex-shrink-0 p-2 rounded-xl',
            colors.bg
          )}>
            <Icon className={cn('w-5 h-5', colors.icon)} strokeWidth={2} />
          </div>

          {/* Text content */}
          <div className="flex-1 min-w-0 pt-0.5">
            {title && (
              <h4 className="text-subheadline font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight mb-0.5">
                {title}
              </h4>
            )}
            <p className="text-body text-light-text-secondary dark:text-dark-text-secondary tracking-tight">
              {message}
            </p>

            {/* Action button */}
            {action && (
              <button
                onClick={action.onClick}
                className={cn(
                  'mt-2 text-subheadline font-semibold tracking-tight',
                  colors.icon,
                  'hover:underline focus:outline-none'
                )}
              >
                {action.label}
              </button>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={() => onClose(id)}
            className={cn(
              'flex-shrink-0 p-1.5 rounded-lg',
              'text-light-text-tertiary dark:text-dark-text-tertiary',
              'hover:text-light-text-primary dark:hover:text-dark-text-primary',
              'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]',
              'transition-all duration-fast',
              'focus:outline-none'
            )}
            aria-label="Close notification"
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {/* Progress bar */}
        {duration > 0 && (
          <div className="h-1 bg-black/[0.04] dark:bg-white/[0.04]">
            <motion.div
              className={cn('h-full', colors.progress)}
              initial={{ width: '100%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.1, ease: 'linear' }}
            />
          </div>
        )}
      </div>
    </motion.div>
  )
}

/**
 * Toast Container - Manages toast stack positioning
 */
interface ToastContainerProps {
  toasts: Array<{
    id: string
    message: string
    type: ToastType
    title?: string
    duration?: number
    action?: { label: string; onClick: () => void }
  }>
  onClose: (id: string) => void
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
}

const positionClasses = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2'
}

export function ToastContainer({
  toasts,
  onClose,
  position = 'bottom-right'
}: ToastContainerProps) {
  return (
    <div className={cn(
      'fixed z-[100] flex flex-col gap-3',
      positionClasses[position],
      position.includes('bottom') ? 'flex-col-reverse' : 'flex-col'
    )}>
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            {...toast}
            onClose={onClose}
          />
        ))}
      </AnimatePresence>
    </div>
  )
}

/**
 * Toast Context and Hook for global toast management
 */
interface ToastContextType {
  showToast: (toast: Omit<ToastProps, 'id' | 'onClose'>) => void
  success: (message: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'type' | 'message'>>) => void
  error: (message: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'type' | 'message'>>) => void
  warning: (message: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'type' | 'message'>>) => void
  info: (message: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'type' | 'message'>>) => void
}

const ToastContext = createContext<ToastContextType | null>(null)

interface ToastProviderProps {
  children: ReactNode
  position?: ToastContainerProps['position']
  maxToasts?: number
}

export function ToastProvider({
  children,
  position = 'bottom-right',
  maxToasts = 5
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Array<{
    id: string
    message: string
    type: ToastType
    title?: string
    duration?: number
    action?: { label: string; onClick: () => void }
  }>>([])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const showToast = useCallback((toast: Omit<ToastProps, 'id' | 'onClose'>) => {
    const id = Math.random().toString(36).substring(2, 11)
    setToasts(prev => {
      const newToasts = [...prev, { ...toast, id }]
      // Limit max toasts
      if (newToasts.length > maxToasts) {
        return newToasts.slice(-maxToasts)
      }
      return newToasts
    })
  }, [maxToasts])

  const success = useCallback((message: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'type' | 'message'>>) => {
    showToast({ type: 'success', message, ...options })
  }, [showToast])

  const error = useCallback((message: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'type' | 'message'>>) => {
    showToast({ type: 'error', message, ...options })
  }, [showToast])

  const warning = useCallback((message: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'type' | 'message'>>) => {
    showToast({ type: 'warning', message, ...options })
  }, [showToast])

  const info = useCallback((message: string, options?: Partial<Omit<ToastProps, 'id' | 'onClose' | 'type' | 'message'>>) => {
    showToast({ type: 'info', message, ...options })
  }, [showToast])

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} position={position} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
